import React, { useMemo } from "react";
import classNames from "classnames";
import { useFormBuilderEditorContext } from "../../../../formBuilderEditorContext";
import { useFormBuilderEntityDomNodeRefMapRegistration } from "../../../../hooks/entityDomNodeRefMap/useFormBuilderEntityDomNodeRefMapRegistration";
import { TFormBuilderEntityViewMoveContext, IUseFormBuilderEntityViewMoveArgs, useFormBuilderEntityViewMove } from "../../../../hooks/entityViewMove/useFormBuilderEntityViewMove";
import { IFormBuilderRow } from "../../../../model/formBuilderRow";
import { formBuilderGenerateItemDesignStyles, TFormBuilderItem } from "../../../../model/item/formBuilderItem";
import { renderFormBuilderItemContent } from "./renderFormBuilderItemContent";

interface IFormBuilderItemViewProps {
  row: IFormBuilderRow;
  item: TFormBuilderItem;
}

interface IFormBuilderItemViewBoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

const ITEM_MOVE_OPACITY = 0.7;
const ITEM_MOVE_Z_INDEX = 99;

export function FormBuilderItemView(props: IFormBuilderItemViewProps) {
  const { row, item } = props;

  const { formBuilderController, formBuilderEntityDomNodeRefMap } = useFormBuilderEditorContext();

  const domNodeRefMapRegistration = useFormBuilderEntityDomNodeRefMapRegistration(item.key, formBuilderEntityDomNodeRefMap);

  const designStyles = formBuilderGenerateItemDesignStyles(item);

  const selected = formBuilderController.selectedEntity?.type === "item" && formBuilderController.selectedEntity.key === item.key;
  const parentSelected = formBuilderController.selectedEntity?.type === "row" && formBuilderController.selectedEntity.key === row.key;
  const siblingSelected = !selected && formBuilderController.selectedEntity?.type === "item" && formBuilderController.selectedEntity.rowKey === row.key;

  const moving = selected && formBuilderController.selectedEntity.editingMode.type === "move";

  const moveContext: TFormBuilderEntityViewMoveContext = useMemo(() => ({
    type: "item",
    entity: item,
    parentRow: row,
  }), [item, row]);

  const viewMoveArgs: IUseFormBuilderEntityViewMoveArgs = useMemo(
    () => ({
      context: moveContext,
      moving,
    }),
    [
      moveContext,
      moving,
    ],
  );

  const { viewMovePosition, viewMoveDimensions, handleViewMovePointerDown } = useFormBuilderEntityViewMove(viewMoveArgs);

  function getBoundingBox(): IFormBuilderItemViewBoundingBox {
    if (moving && viewMovePosition != null && viewMoveDimensions != null) {
      return {
        x: viewMovePosition.x,
        y: viewMovePosition.y,
        width: viewMoveDimensions.width,
        height: viewMoveDimensions.height,
      };
    } else {
      return null;
    }
  }

  const boundingBox = getBoundingBox();

  const detached = moving && boundingBox != null;
  
  return (
    <>
      <div
        ref={domNodeRefMapRegistration}
        className={classNames(designStyles.className, "builder-item", {
          "builder-item-selected": selected,
        })}
        style={{
          ...designStyles.styleProperties,
          ...(detached ? {
            position: "absolute",
            left: boundingBox.x,
            top: boundingBox.y,
            width: boundingBox.width,
            height: boundingBox.height,
            opacity: ITEM_MOVE_OPACITY,
            zIndex: ITEM_MOVE_Z_INDEX,
          } : {}),
        }}
        onClick={() => {
          if (!selected && (parentSelected || siblingSelected)) {
            formBuilderController.selectEntity({
              selectedEntity: {
                type: "item",
                rowKey: row.key,
                key: item.key,
                editingMode: {
                  type: "none",
                },
              },
            });
          }
        }}
        onPointerDown={e => {
          e.preventDefault();
          
          if (selected) {
            handleViewMovePointerDown(e);
          }
        }}
      >
        {renderFormBuilderItemContent(item)}
      </div>

      {detached && (
        <div
          style={{
            visibility: "hidden",
            width: boundingBox.width,
            height: boundingBox.height,
          }}
        />
      )}
    </>
  );
}
